import type { SelectTagInSearchModalParams } from '@/wabunka-src/common/utils/googleAnalytics/common/selectTag';
import type { AbTestAssignParams } from '@/wabunka-src/common/utils/googleAnalytics/events/abTestAssign';
import type { AddGuestInfoParams } from '@/wabunka-src/common/utils/googleAnalytics/events/addGuestInfo';
import type { AddPaymentCardParams } from '@/wabunka-src/common/utils/googleAnalytics/events/addPaymentCard';
import type { BeginCheckoutParams } from '@/wabunka-src/common/utils/googleAnalytics/events/beginCheckout';
import type { ChangeCurrencyParams } from '@/wabunka-src/common/utils/googleAnalytics/events/changeCurrency';
import type { ChangeSortParams } from '@/wabunka-src/common/utils/googleAnalytics/events/changeSort';
import type { WabunkaCheckAvailabilityParams } from '@/wabunka-src/common/utils/googleAnalytics/events/checkAvailability';
import type { ClickAddFiltersParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickAddFilters';
import type { ClickHeaderNavParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickHeaderNav';
import type { ClickInstantBookingParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickInstantBooking';
import type { ClickRecommendationParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickRecommendation';
import type { ClickSelectDatesParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickSelectDates';
import type { ClickShowRequestItineraryArrangementsParams } from '@/wabunka-src/common/utils/googleAnalytics/events/clickShowRequestItineraryArrangements';
import type { ContactUsParams } from '@/wabunka-src/common/utils/googleAnalytics/events/contactUs';
import type { DrawerImpressionParams } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerImpression';
import type { DrawerOpenParams } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerOpen';
import type { DrawerTabViewParams } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerTabView';
import type { FeatureViewParams } from '@/wabunka-src/common/utils/googleAnalytics/events/featureView';
import type { FeaturedAreaClickParams } from '@/wabunka-src/common/utils/googleAnalytics/events/featuredAreaClick';
import type { LocationDetailsOpenParams } from '@/wabunka-src/common/utils/googleAnalytics/events/locationDetailsOpen';
import type { MapAreaLayerEnterParams } from '@/wabunka-src/common/utils/googleAnalytics/events/mapAreaLayerEnter';
import type { MapPinClickParams } from '@/wabunka-src/common/utils/googleAnalytics/events/mapPinClick';
import type { MapPreviewCtaClickParams } from '@/wabunka-src/common/utils/googleAnalytics/events/mapPreviewCtaClick';
import type { MapViewOpenParams } from '@/wabunka-src/common/utils/googleAnalytics/events/mapViewOpen';
import type { MapViewportSearchParams } from '@/wabunka-src/common/utils/googleAnalytics/events/mapViewportSearch';
import type { ReservationQuestionAnsweredParams } from '@/wabunka-src/common/utils/googleAnalytics/events/reservationQuestionAnswered';
import type { ReservationQuestionsCompleteParams } from '@/wabunka-src/common/utils/googleAnalytics/events/reservationQuestionsComplete';
import type { SearchExperienceByDateParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByDate';
import type { SearchExperienceByDestinationParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByDestination';
import type { SearchExperienceByPreferenceParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByPreference';
import type { SearchExperienceByThemeParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByTheme';
import type { SearchExperienceByTimeOfDayParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchExperienceByTimeOfDay';
import type { SearchResultClickParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchResultClick';
import type { SearchResultHitCountParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchResultHitCount';
import type { SearchResultsShownParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchResultsShown';
import type { SearchZeroResultParams } from '@/wabunka-src/common/utils/googleAnalytics/events/searchZeroResult';
import type { WabunkaSectionSlugViewParams } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import type { SectionViewAllParams } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionViewAll';
import type { SelectEventParams } from '@/wabunka-src/common/utils/googleAnalytics/events/selectEvent';
import type { SelectExperienceInListParams } from '@/wabunka-src/common/utils/googleAnalytics/events/selectExperienceInList';
import type { StoryViewParams } from '@/wabunka-src/common/utils/googleAnalytics/events/storyView';
import type { VideoCtaClickParams } from '@/wabunka-src/common/utils/googleAnalytics/events/videoCtaClick';
import type { VideoPlayParams } from '@/wabunka-src/common/utils/googleAnalytics/events/videoPlay';
import type { ViewEventListParams } from '@/wabunka-src/common/utils/googleAnalytics/events/viewEventList';
import type { ViewExperienceListParams } from '@/wabunka-src/common/utils/googleAnalytics/events/viewExperienceList';
import type { WabunkaPurchaseParams } from '@/wabunka-src/common/utils/googleAnalytics/events/wabunkaPurchase';
import { captureException } from '@/wabunka-src/common/utils/sentry';
import type {
  AiChatAcceptTermsParams,
  AiChatClickNavigationParams,
  AiChatCloseParams,
  AiChatErrorParams,
  AiChatOpenParams,
  AiChatReceiveResponseParams,
  AiChatSelectTagParams,
  AiChatSendMessageParams,
} from '@/wabunka-src/features/Chat/utils/googleAnalytics/chatEvents';
import type { FaqToggleOpenParams } from '@/wabunka-src/features/FAQ/composables/useFaqTracking';

export type Device = 'PC' | 'SP';
export type Currency = 'JPY';

type NoGAEventParams = Record<string, never>;
export const noGAEventParams: NoGAEventParams = {};

/** イベント名に対応するパラメーターの型定義 */
interface WabunkaGoogleAnalyticsEvents {
  add_guest_info: AddGuestInfoParams;
  add_payment_card: AddPaymentCardParams;
  begin_checkout: BeginCheckoutParams;
  change_currency: ChangeCurrencyParams;
  change_sort: ChangeSortParams;
  purchase: WabunkaPurchaseParams;
  map_view_open: MapViewOpenParams;
  map_pin_click: MapPinClickParams;
  map_preview_cta_click: MapPreviewCtaClickParams;
  map_viewport_search: MapViewportSearchParams;
  map_area_layer_enter: MapAreaLayerEnterParams;
  search_experience_by_theme: SearchExperienceByThemeParams;
  search_experience_by_date: SearchExperienceByDateParams;
  search_experience_by_destination: SearchExperienceByDestinationParams;
  search_experience_by_preference: SearchExperienceByPreferenceParams;
  search_experience_by_time_of_day: SearchExperienceByTimeOfDayParams;
  search_result_hit_count: SearchResultHitCountParams;
  search_modal_open: NoGAEventParams;
  search_input_started: NoGAEventParams;
  search_results_shown: SearchResultsShownParams;
  search_zero_result: SearchZeroResultParams;
  search_result_click: SearchResultClickParams;
  select_event: SelectEventParams;
  select_experience_in_list: SelectExperienceInListParams;
  select_theme_in_search_modal: SelectTagInSearchModalParams;
  select_destination_in_search_modal: SelectTagInSearchModalParams;
  select_preference_in_search_modal: SelectTagInSearchModalParams;
  view_event_list: ViewEventListParams;
  view_experience_list: ViewExperienceListParams;
  story_view: StoryViewParams;
  feature_view: FeatureViewParams;
  check_availability: WabunkaCheckAvailabilityParams;
  section_slug_view: WabunkaSectionSlugViewParams;
  section_view_all: SectionViewAllParams;
  featured_area_click: FeaturedAreaClickParams;
  ab_test_assign: AbTestAssignParams;
  reservation_question_answered: ReservationQuestionAnsweredParams;
  reservation_questions_complete: ReservationQuestionsCompleteParams;
  location_details_open: LocationDetailsOpenParams;
  contact_us: ContactUsParams;
  drawer_impression: DrawerImpressionParams;
  drawer_open: DrawerOpenParams;
  drawer_tab_view: DrawerTabViewParams;
  click_add_filters: ClickAddFiltersParams;
  click_instant_booking_filter: ClickInstantBookingParams;
  click_recommendation: ClickRecommendationParams;
  click_select_dates: ClickSelectDatesParams;
  click_show_request_itinerary_arrangements: ClickShowRequestItineraryArrangementsParams;
  faq_toggle_open: FaqToggleOpenParams;
  video_play: VideoPlayParams;
  video_cta_click: VideoCtaClickParams;
  [key: `click_header_nav_${string}`]: ClickHeaderNavParams;
  ai_chat_open: AiChatOpenParams;
  ai_chat_close: AiChatCloseParams;
  ai_chat_send_message: AiChatSendMessageParams;
  ai_chat_receive_response: AiChatReceiveResponseParams;
  ai_chat_select_tag: AiChatSelectTagParams;
  ai_chat_click_navigation: AiChatClickNavigationParams;
  ai_chat_error: AiChatErrorParams;
  ai_chat_accept_terms: AiChatAcceptTermsParams;
  [key: `click_top_category_card_${string}`]: NoGAEventParams;
}

type DataLayerWindow = Window & { dataLayer?: Record<string, unknown>[] };

/**
 * GTM へ送るための dataLayer を用意する。
 *
 * ssr: false のルートでは GTM スニペットがクライアント注入となり生成が page mount に間に合わないため、
 * スニペットではなく plugin 段階でここを呼んで配列の存在を担保する。gtm.js ロード前の push はキューされる。
 */
export const initWabunkaDataLayer = () => {
  if (typeof window === 'undefined') return;
  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer ?? [];
};

/**
 * GA4 標準の ecommerce 構造へ変換して送るイベント。
 * 汎用GA4タグは dataLayer.ecommerce から売上系を読むため、平坦なまま push すると
 * 購入額・税・注文ID・items が丸ごと落ちる（イベント数は一致するため集計比較では検出できない）。
 */
const GTM_ECOMMERCE_EVENTS = new Set<string>(['begin_checkout', 'purchase', 'add_guest_info', 'add_payment_card']);

/** ecommerce オブジェクト配下へ入れる標準キー。これ以外の param はトップレベルに残す */
const ECOMMERCE_KEYS = new Set<string>(['transaction_id', 'value', 'tax', 'currency', 'items']);

/** 直前の push が積んだトップレベル param キー。次の push でこれらを undefined にリセットし、DLV の持ち越しを防ぐ */
let prevKeys: string[] = [];

/**
 * 平坦な params を ecommerce 標準キーとそれ以外に振り分ける。
 * 振り分けの既定はトップレベル側。新しい param が黙って売上系として解釈されるより、
 * パラメータ対応表への登録漏れとして DebugView で気づける方に倒す。
 */
const splitEcommerceParams = (params: Record<string, unknown>) => {
  const ecommerce: Record<string, unknown> = {};
  const topLevel: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (ECOMMERCE_KEYS.has(key)) {
      ecommerce[key] = value;
    } else {
      topLevel[key] = value;
    }
  });
  return { ecommerce, topLevel };
};

/**
 * イベントを native dataLayer push で GTM へ送る送信口。
 * - 直前キーを undefined でリセットしてから params を重ね、DLV の持ち越しを防ぐ
 * - ecommerce 系は ecommerce オブジェクトへ変換し、前後を null でクリアする
 *
 * ecommerce のネストは GTM の dataLayer 規約であるため、構造変換はこの中だけで行い、イベント定義側は平坦に保つ。
 */
const pushToDataLayer = (action: string, params: Record<string, unknown> = {}) => {
  // SSR では window 不在。送信先が無いため何もしない
  if (typeof window === 'undefined') return;
  const w = window as DataLayerWindow;
  // plugin が初期化済みの想定だが、初期化前に呼ばれてもイベントを落とさないよう配列を用意してから push する
  const dataLayer = (w.dataLayer = w.dataLayer ?? []);
  const isEcommerce = GTM_ECOMMERCE_EVENTS.has(action);
  const { ecommerce, topLevel } = isEcommerce ? splitEcommerceParams(params) : { ecommerce: undefined, topLevel: params };
  const reset = Object.fromEntries(prevKeys.map((key) => [key, undefined]));

  if (isEcommerce) {
    // 前クリア: GTM は push を再帰的にマージするため、クリアしないと前イベントの ecommerce が今回の値に混ざる
    dataLayer.push({ ecommerce: null });
  }
  try {
    dataLayer.push({ event: action, ...reset, ...topLevel, ...(isEcommerce ? { ecommerce } : {}) });
  } finally {
    // 本体 push が throw しても後クリアと prevKeys 更新は必ず通す。
    // GTM は push でデータモデルを更新してからタグを評価するため、途中で throw されると ecommerce が載ったまま残る。
    // 呼び出し元の catch は例外を握るだけで残留を解消しないため、ここで閉じる
    if (isEcommerce) {
      // 後クリア: 汎用GA4タグは全カスタムイベントで ecommerce を読むため、残すと後続イベントに items / value が乗る。
      // 広告CVタグも購入額をここから解決するので、残留は空値化ではなく無関係なCVへの水増しとして現れる
      dataLayer.push({ ecommerce: null });
    }
    // ecommerce はリセット対象に含めない。ネストしたオブジェクトへの undefined ではなく上の null クリアで管理する
    prevKeys = Object.keys(topLevel);
  }
};

/**
 * GA にイベントを送信する
 *
 * @param action イベント名
 * @param eventParams イベントパラメーター
 */
export const sendWabunkaGAEvent = <T extends keyof WabunkaGoogleAnalyticsEvents>(action: T, eventParams: WabunkaGoogleAnalyticsEvents[T]) => {
  try {
    pushToDataLayer(action, eventParams as Record<string, unknown>);
  } catch (error) {
    // イベント送信に失敗した場合エラーをSentryに送信する
    const newError = new Error('Failed to send GA event: ' + (error as Error).message);
    captureException(newError, 'wabunka');
  }
};

/**
 * GA にイベントを送信する
 *
 * @param action イベント名
 * @param eventParams イベントパラメーター
 */
export const sendWabunkaGADynamicEvent = (action: string, eventParams?: Record<string, unknown>) => {
  try {
    pushToDataLayer(action, eventParams ?? {});
  } catch (error) {
    // イベント送信に失敗した場合エラーをSentryに送信する
    const newError = new Error('Failed to send GA event: ' + (error as Error).message);
    captureException(newError, 'wabunka');
  }
};
